import { useCallback, useEffect, useState } from 'react';
import {
  cambiarEstadoCuenta,
  crearCuenta,
  editarCuenta,
  eliminarCuenta,
  ETIQUETA_TIPO_CUENTA,
  listarCuentas,
  listarMediosPagoDian,
  MEDIO_DIAN_SUGERIDO,
  TIPOS_CUENTA,
  type Cuenta,
  type MedioPagoDian,
  type TipoCuenta,
} from '../../lib/api-cuentas';
import {
  Boton,
  Campo,
  CampoLista,
  EncabezadoTabla,
  FilaAcciones,
  FilaTabla,
  MensajeError,
  TablaAncha,
  Tarjeta,
  ThAcciones,
} from '../../ui/base';
import { Modal } from '../../ui/Modal';
import { BotonesFila, ConfirmarEliminar, EncabezadoSeccion, EstadoActivo } from './partes';

type Edicion = { cuenta: Cuenta | null } | null;

/** Dónde entra el dinero. El efectivo no se desactiva ni se elimina: el cuadre lo cuenta. */
export function SeccionCuentas({ negocioId }: { negocioId: string }) {
  const [cuentas, setCuentas] = useState<Cuenta[]>([]);
  const [medios, setMedios] = useState<MedioPagoDian[]>([]);
  const [edicion, setEdicion] = useState<Edicion>(null);
  const [aEliminar, setAEliminar] = useState<Cuenta | null>(null);
  const [aviso, setAviso] = useState('');
  const [errorEliminar, setErrorEliminar] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    listarCuentas(negocioId, false)
      .then(setCuentas)
      .catch(() => setAviso('No pudimos cargar las cuentas.'));
  }, [negocioId]);
  useEffect(cargar, [cargar]);
  useEffect(() => {
    listarMediosPagoDian()
      .then(setMedios)
      .catch(() => setMedios([]));
  }, []);

  async function hacer(accion: () => Promise<void>, alFallar: (m: string) => void): Promise<boolean> {
    setOcupado(true);
    try {
      await accion();
      cargar();
      return true;
    } catch (e) {
      alFallar(e instanceof Error ? e.message : 'No se pudo guardar.');
      return false;
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Tarjeta>
      <EncabezadoSeccion
        titulo="Cuentas"
        ayuda="Dónde entra el dinero de tus ventas. Al cerrar el día se concilia cada una por separado."
        agregar="Agregar cuenta"
        onAgregar={() => setEdicion({ cuenta: null })}
        e2eAgregar="nueva-cuenta"
      />
      <TablaAncha data-e2e="tabla-cuentas">
        <EncabezadoTabla>
          <th className="py-2 text-left">Nombre</th>
          <th className="py-2 text-left">Tipo</th>
          <th className="py-2 text-left">Medio DIAN</th>
          <th className="py-2 text-left">Estado</th>
          <ThAcciones />
        </EncabezadoTabla>
        <tbody>
          {cuentas.map((c) => (
            <FilaTabla key={c.id}>
              <td className="py-2 pr-2 font-semibold">{c.nombre}</td>
              <td className="py-2 pr-2">{ETIQUETA_TIPO_CUENTA[c.tipo]}</td>
              <td className="num py-2 pr-2">{c.medioPagoDian}</td>
              <td className="py-2 pr-2">
                <EstadoActivo activo={c.activa} femenino />
              </td>
              <td className="py-2">
                <BotonesFila
                  nombre={c.nombre}
                  activo={c.activa}
                  ocupado={ocupado}
                  onEditar={() => setEdicion({ cuenta: c })}
                  onEstado={
                    c.esEfectivo ? undefined : () => void hacer(() => cambiarEstadoCuenta(negocioId, c.id, !c.activa), setAviso)
                  }
                  onEliminar={
                    c.esEfectivo
                      ? undefined
                      : () => {
                          setErrorEliminar('');
                          setAEliminar(c);
                        }
                  }
                />
              </td>
            </FilaTabla>
          ))}
        </tbody>
      </TablaAncha>
      <MensajeError>{aviso}</MensajeError>

      <VentanaCuenta
        edicion={edicion}
        medios={medios}
        ocupado={ocupado}
        onCerrar={() => setEdicion(null)}
        onGuardar={(datos, alFallar) => {
          const cuenta = edicion?.cuenta ?? null;
          void hacer(
            () => (cuenta ? editarCuenta(negocioId, cuenta.id, datos) : crearCuenta(negocioId, datos.nombre, datos.tipo, datos.medioPagoDian)),
            alFallar
          ).then((ok) => ok && setEdicion(null));
        }}
      />
      <ConfirmarEliminar
        titulo={aEliminar ? `¿Eliminar la cuenta «${aEliminar.nombre}»?` : null}
        puntos={[
          'Deja de salir en el mostrador y en el cuadre de los días que vienen.',
          `Las ventas ya hechas siguen diciendo «${aEliminar?.nombre.toLowerCase() ?? ''}».`,
        ]}
        ocupado={ocupado}
        error={errorEliminar}
        onCerrar={() => setAEliminar(null)}
        onDesactivar={
          aEliminar?.activa
            ? () =>
                void hacer(() => cambiarEstadoCuenta(negocioId, aEliminar.id, false), setErrorEliminar).then(
                  (ok) => ok && setAEliminar(null)
                )
            : undefined
        }
        onEliminar={() =>
          aEliminar &&
          void hacer(() => eliminarCuenta(negocioId, aEliminar.id), setErrorEliminar).then((ok) => ok && setAEliminar(null))
        }
      />
    </Tarjeta>
  );
}

function VentanaCuenta({
  edicion,
  medios,
  ocupado,
  onCerrar,
  onGuardar,
}: {
  edicion: Edicion;
  medios: MedioPagoDian[];
  ocupado: boolean;
  onCerrar: () => void;
  onGuardar: (
    datos: { nombre: string; tipo: TipoCuenta; medioPagoDian: string },
    alFallar: (mensaje: string) => void
  ) => void;
}) {
  const cuenta = edicion?.cuenta ?? null;
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<TipoCuenta>('billetera');
  const [medio, setMedio] = useState(MEDIO_DIAN_SUGERIDO.billetera);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!edicion) return;
    setNombre(cuenta?.nombre ?? '');
    setTipo(cuenta?.tipo ?? 'billetera');
    setMedio(cuenta?.medioPagoDian ?? MEDIO_DIAN_SUGERIDO.billetera);
    setError('');
  }, [edicion, cuenta]);

  // La cuenta de efectivo conserva su tipo; una cuenta nueva no puede ser de efectivo, que ya existe.
  const tipos = cuenta?.esEfectivo ? (['efectivo'] as TipoCuenta[]) : TIPOS_CUENTA.filter((t) => t !== 'efectivo');

  return (
    <Modal abierto={edicion !== null} titulo={cuenta ? 'Editar cuenta' : 'Agregar cuenta'} onCerrar={onCerrar}>
      <div className="space-y-4" data-e2e="ventana-cuenta">
        <div className="grid gap-3 sm:grid-cols-3">
          <Campo
            etiqueta="Nombre"
            placeholder="Nequi de la tienda"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            data-e2e="cuenta-nombre"
          />
          <CampoLista
            etiqueta="Tipo"
            value={tipo}
            disabled={cuenta?.esEfectivo}
            onChange={(e) => {
              const t = e.target.value as TipoCuenta;
              setTipo(t);
              setMedio(MEDIO_DIAN_SUGERIDO[t]);
            }}
          >
            {tipos.map((t) => (
              <option key={t} value={t}>
                {ETIQUETA_TIPO_CUENTA[t]}
              </option>
            ))}
          </CampoLista>
          <CampoLista etiqueta="Medio de pago DIAN" value={medio} onChange={(e) => setMedio(e.target.value)} data-e2e="cuenta-medio-dian">
            {medios.map((m) => (
              <option key={m.codigo} value={m.codigo}>
                {m.codigo} · {m.nombre}
              </option>
            ))}
          </CampoLista>
        </div>
        {cuenta && <p className="text-muted text-contenido">Las ventas ya hechas conservan el nombre con el que se vendieron.</p>}
        <MensajeError>{error}</MensajeError>
        <FilaAcciones>
          <Boton variante="contorno" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            disabled={ocupado || nombre.trim().length < 2}
            onClick={() => onGuardar({ nombre, tipo, medioPagoDian: medio }, setError)}
            data-e2e="agregar-cuenta"
          >
            {cuenta ? 'Guardar cambios' : 'Agregar cuenta'}
          </Boton>
        </FilaAcciones>
      </div>
    </Modal>
  );
}
